// eslint-disable-next-line no-restricted-syntax
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import isBetween from 'dayjs/plugin/isBetween';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import minMax from 'dayjs/plugin/minMax';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isToday from 'dayjs/plugin/isToday';
import { locale as defaultLocale } from '@/utils/_shared/i18n';
import { AvailableLocale } from '@/i18n/LanguageProvider/shared';
import { getLanguageCode, normalizeLocale } from '@/utils/translations';

dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(minMax);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isToday);

export default dayjs;

export const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Depending on the browser language detected, this is used to help figure out which dayjs locale
// to import and use it dynamically in order to have the dates formatted according to the locale
//
// ** We'll need to manually add in the necessary locale files as we go **
//
// Optimized dayjs locale loading with explicit imports that Vite can analyze
const LOCALE_IMPORTS = {
  en: () => import('dayjs/locale/en'),
  'en-gb': () => import('dayjs/locale/en-gb'),
  fr: () => import('dayjs/locale/fr'),
  es: () => import('dayjs/locale/es'),
  it: () => import('dayjs/locale/it'),
  pl: () => import('dayjs/locale/pl'),
  pt: () => import('dayjs/locale/pt'),
  ht: () => import('dayjs/locale/ht'),
  de: () => import('dayjs/locale/de'),
} as const;

type SupportedLocale = keyof typeof LOCALE_IMPORTS;

/**
 * Resolves the dayjs locale file that formats dates for an app locale.
 * A region keeps its own file only where it changes the format: en-GB is
 * day-first and 24-hour, so it cannot share the bundled en. Every other
 * region falls back to its language, which is why fr-CA formats as fr.
 * Persisted locales may use underscores (fr_FR), so normalize first.
 */
export const getDayjsLocale = (
  locale?: AvailableLocale | string,
): SupportedLocale => {
  const normalized = normalizeLocale(locale || defaultLocale);
  const region = normalized.toLowerCase() as SupportedLocale;

  return region in LOCALE_IMPORTS
    ? region
    : (getLanguageCode(normalized) as SupportedLocale);
};

export const loadDayjsLocale = async (locale?: AvailableLocale) => {
  const localeToUse = getDayjsLocale(locale);

  if (localeToUse === 'en') {
    // English is bundled, but it must be applied after switching from another locale.
    dayjs.locale('en');
    return true;
  }

  // Check if we have an explicit import for this locale
  const localeImport = LOCALE_IMPORTS[localeToUse];
  if (!localeImport) {
    console.warn(
      `Locale '${localeToUse}' is not supported. Supported locales are: ${Object.keys(LOCALE_IMPORTS).join(', ')}`,
    );
    return false;
  }

  try {
    // Dynamically import the locale using explicit import function
    await localeImport();

    // This is what formats the date correctly according to the locale
    dayjs.locale(localeToUse);
    return true;
  } catch (error) {
    console.error(
      `Failed to dynamically load the locale '${localeToUse}'. Error:`,
      error,
    );
    return false;
  }
};

/**
 * Loads a locale before consumers construct adapters that read its Dayjs data.
 * Ready means the requested language has finished loading, not that some
 * earlier locale did.
 */
export const useDayjsLocale = (locale?: AvailableLocale) => {
  const requestedLocale = getDayjsLocale(locale);
  const [loadedLocale, setLoadedLocale] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    loadDayjsLocale(locale).then(loaded => {
      if (!cancelled) {
        setLoadedLocale(loaded ? requestedLocale : null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [locale, requestedLocale]);

  return loadedLocale === requestedLocale;
};
